import React, { useState, useEffect } from 'react';
import { fetchCustomerWorkspaces } from '../services/backendApiService';
import { generateWorkspaceHeuristicsAuditReportWithProgress } from '../services/backendExcelService';
import { CustomerWorkspace } from '../types';
import { TokenService } from '../services/tokenService';

const WorkspaceHeuristicsAudit: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<CustomerWorkspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedWorkspaceKey, setSelectedWorkspaceKey] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgressMessage, setDownloadProgressMessage] = useState('');
  const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState('');
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState(false);

  // Auto-load workspaces on component mount
  useEffect(() => {
    if (TokenService.hasValidToken()) {
      console.log('🔄 Auto-running fetch workspaces with cached token');
      handleFetchWorkspaces();
    }
  }, []);

  // Listen for token invalidation and clear state
  useEffect(() => {
    const checkTokenValidity = () => {
      const currentToken = TokenService.getToken();
      if (!currentToken) {
        console.log('🔒 Token was invalidated, clearing app state');
        setWorkspaces([]);
        setSelectedWorkspace('');
        setSelectedWorkspaceKey('');
        setError('');
      }
    };
    const interval = setInterval(checkTokenValidity, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFetchWorkspaces = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await fetchCustomerWorkspaces();
      setWorkspaces(result);
      console.log('✅ Workspaces loaded successfully');
    } catch (err) {
      console.error('❌ Error fetching workspaces:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWorkspaceSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    setWorkspaceSearchTerm(searchTerm);
    setSelectedWorkspace('');
    setSelectedWorkspaceKey('');
    setShowWorkspaceDropdown(true);
  };

  const handleWorkspaceSelect = (workspace: CustomerWorkspace) => {
    const displayName = `${workspace.name} (${workspace.key})`;
    setSelectedWorkspace(displayName);
    setSelectedWorkspaceKey(workspace.key || '');
    setWorkspaceSearchTerm(displayName);
    setShowWorkspaceDropdown(false);
  };

  const handleDownloadReport = async () => {
    if (!selectedWorkspaceKey) {
      setError('Please select a workspace first');
      return;
    }

    setIsDownloading(true);
    setError('');
    setDownloadProgressMessage('Starting...');

    try {
      await generateWorkspaceHeuristicsAuditReportWithProgress(
        selectedWorkspaceKey,
        (progressMessage: string) => {
          // Update progress display
          console.log('🔄 onProgress called with:', progressMessage);
          setDownloadProgressMessage(progressMessage);
          console.log('🔄 State should now be:', progressMessage);
        }
      );
      
      console.log('✅ Workspace heuristics audit report downloaded successfully');
    } catch (err) {
      console.error('❌ Error downloading workspace heuristics audit report:', err);
      setError(err instanceof Error ? err.message : 'Failed to download workspace heuristics audit report');
    } finally {
      setIsDownloading(false);
      setDownloadProgressMessage('');
    }
  };

  // Filter workspaces based on search term
  const filteredWorkspaces = workspaces.filter(workspace => {
    const searchLower = workspaceSearchTerm.toLowerCase();
    const nameMatch = workspace.name?.toLowerCase().includes(searchLower);
    const keyMatch = workspace.key?.toLowerCase().includes(searchLower);
    return nameMatch || keyMatch;
  });

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.workspace-search-container')) {
        setShowWorkspaceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="workspace-heuristics-audit">
      <div className="workspace-heuristics-audit-container">
        {/* Workspace Selection */}
        <div className="workspace-selection-card">
          <div className="selection-row">
            <div className="workspace-field">
              <label htmlFor="workspace-search">Search Customer Workspace:</label>
              <div className="workspace-search-container">
                <input
                  id="workspace-search"
                  type="text"
                  value={workspaceSearchTerm}
                  onChange={handleWorkspaceSearchChange}
                  placeholder="Type to search for a customer workspace..."
                  className="workspace-search-input"
                  disabled={isLoading}
                />
                {showWorkspaceDropdown && filteredWorkspaces.length > 0 && (
                  <div className="workspace-dropdown">
                    {filteredWorkspaces.map((workspace) => (
                      <div
                        key={workspace.key}
                        className="workspace-option"
                        onClick={() => handleWorkspaceSelect(workspace)}
                      >
                        <div className="workspace-name">{workspace.name}</div>
                        <div className="workspace-id">ID: {workspace.key}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="download-field">
              <label htmlFor="download-button"> </label>
              <button
                id="download-button"
                onClick={handleDownloadReport}
                className="download-button"
                disabled={!selectedWorkspaceKey || isDownloading}
              >
                {(() => {
                  const buttonText = isDownloading 
                    ? downloadProgressMessage || 'Starting...'
                    : '📥 Download Audit Report';
                  console.log('🔘 Button rendering with text:', buttonText, 'loading:', isDownloading, 'message:', downloadProgressMessage);
                  return buttonText;
                })()}
              </button>
            </div>
          </div>

          {selectedWorkspace && (
            <div className="selected-workspace-info">
              <h3>Selected Workspace:</h3>
              <p><strong>Name:</strong> {selectedWorkspace}</p>
              <p><strong>Key:</strong> {selectedWorkspaceKey}</p>
            </div>
          )}
        </div>

        {/* Loading and Error States */}
        {isLoading && (
          <div className="loading">
            🔄 Loading customer workspaces...
          </div>
        )}

        {error && (
          <div className="error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceHeuristicsAudit; 